import { Image } from "@/components/common/Image";
import { useToastPromise } from "@/hooks/useToast";
import { mutateSession } from "@/hooks/useUser";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  Input,
  Link as StyledLink,
  Text,
  InputGroup,
  InputRightElement,
  Icon,
  Tooltip,
} from "@chakra-ui/react";
import { Eye, EyeOff } from "react-feather";
import axios from "axios";
import { signIn, signOut } from "next-auth/client";
import { useTranslation } from "next-i18next";
import getConfig from "next/config";
import Link from "next/link";
import { useRouter } from "next/router";
import React, { useState } from "react";
import { useForm } from "react-hook-form";

import { updateProfile } from "./withAuthentication";

type Inputs = {
  email: string;
  password: string;
};

type Props = {
  providers: [];
  token?: string;
  office?: {
    id?: number;
    name?: string;
    representative?: string;
    url?: string;
    officeEmail?: string;
    phone?: number;
    address?: string;
    city?: string;
    postalCode?: string;
    country?: string;
    type?: string;
  };
  page?: boolean;
};

const Signin = ({
  providers,
  token = null,
  page = false,
  office = null,
}: Props): React.ReactElement => {
  const {
    handleSubmit,
    formState: { errors, isSubmitting },
    register,
    watch,
    reset,
  } = useForm<Inputs>();
  const router = useRouter();
  const [error, setError] = useState(router?.query?.error);
  const [success, setSuccess] = useState(false);
  const { t } = useTranslation("common");
  const watchEmail = watch("email");
  const toast = useToastPromise();
  const [showPassword, setShowPassword] = useState(false);

  const handleChangeRole = React.useCallback(async (id, type, role = null) => {
    const profile = {
      event: null,
      role: type === "user" ? role : null,
      pressOffice: type === "press" ? id : null,
      editorialOffice: type === "editorial" ? id : null,
      organization: type === "organization" ? id : null,
    };

    await updateProfile(profile);
    await mutateSession();
  }, []);

  const onSubmit = async (data) => {
    const res = await signIn("credentials", { ...data, redirect: false });
    await mutateSession();

    if (office?.id) {
      let officeRes;
      const locale = router?.locale;
      await toast.promise(
        axios
          .post("/editorial-office", { ...office, token, locale })
          .then((res) => {
            officeRes = res;
          })
          .catch(async (error) => {
            await signOut();
            await mutateSession();
            if (error === "Name taken") {
              router.push({
                path: `/signup/office/${office?.id}`,
                query: { nameTaken: true },
              });
              return;
            }
          })
      );
    }
    await mutateSession();

    if (router?.asPath?.includes("2Faccreditation%2Fevent")) {
      handleChangeRole(undefined, "user", "user");
    }
    if (res.error) {
      setSuccess(false);
      setError(res.error);
    } else {
      setSuccess(true);
      setError(null);
      reset();
    }
  };

  const onSignin = async (provider: string) => {
    const res = await signIn(provider, { redirect: false });
    await mutateSession();

    if (res?.error) {
      setSuccess(false);
      setError(res?.error);
    }
  };

  const { publicRuntimeConfig } = getConfig();
  const errorKeys = {
    WrongCredentials: t("errorKeys.wrongCredentials"),
    NotActivated: t("errorKeys.notActivated"),
    ServerError: t("errorKeys.serverError"),
    Banned: t("errorKeys.banned"),
    "Email taken": t("users.emailTaken"),
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        {error && (
          <Alert status={error === "NotActivated" ? "info" : "error"} mb="20px">
            <AlertIcon />
            {errorKeys[error] ?? t("users.signinError")}
          </Alert>
        )}
        {success && (
          <Alert status="success" mb="20px">
            <AlertIcon />
            {t("users.signinSuccess")}
          </Alert>
        )}
        <FormControl isInvalid={!!errors.email} isRequired mb="15px">
          {page && <FormLabel>{t("users.email")}</FormLabel>}
          <Input
            size={page ? "md" : "lg"}
            placeholder={page ? "" : t("users.email")}
            autoFocus
            type="email"
            autoComplete="username"
            data-cy="email-field"
            {...register("email", { required: true })}
          />
          {errors.email && (
            <FormErrorMessage>{t("users.fieldRequired")}</FormErrorMessage>
          )}
        </FormControl>
        <FormControl isInvalid={!!errors.password} isRequired mb="15px">
          {page && <FormLabel>{t("users.password")}</FormLabel>}
          <InputGroup>
            <Input
              size={page ? "md" : "lg"}
              placeholder={page ? "" : t("users.password")}
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              data-cy="password-field"
              {...register("password", { required: true })}
            />
            <InputRightElement>
              <Tooltip
                label={showPassword ? t("hide") : t("show")}
                placement="top"
              >
                <Icon
                  as={showPassword ? EyeOff : Eye}
                  w={5}
                  h={5}
                  mt={3}
                  mr={3}
                  cursor="pointer"
                  onClick={() => setShowPassword(!showPassword)}
                  transition={"all .25s ease-in-out"}
                  _hover={{
                    opacity: 0.7,
                  }}
                  color="GrayText"
                />
              </Tooltip>
            </InputRightElement>
          </InputGroup>
          {errors.password && (
            <FormErrorMessage>{t("users.fieldRequired")}</FormErrorMessage>
          )}
        </FormControl>
        <Text textAlign="right">
          <Link
            href={{
              pathname: "/forgot-password",
              query: { email: watchEmail },
            }}
          >
            <StyledLink data-cy="lost-password-link">
              {t("users.lostPassword")}
            </StyledLink>
          </Link>
        </Text>
        <Flex mt="2rem" justify="center">
          <Button
            type="submit"
            size={page ? "md" : "xl"}
            mr={page ? 0 : ".75rem"}
            isLoading={isSubmitting}
            data-cy="signin-button"
            w={page ? "full" : "auto"}
          >
            {t("users.signin")}
          </Button>
          {!page && (
            <Link href={`/signup/${token || ""}`}>
              <Button
                variant="outline"
                size={page ? "md" : "xl"}
                data-cy="signup-button"
              >
                {t("users.signup")}
              </Button>
            </Link>
          )}
        </Flex>
      </form>
      <Divider mt="1.5rem" mb=".75rem" />
      <Text textAlign="center">{t("users.signinSignupWith")}</Text>
      {Object.values(providers).map(
        (provider: { id: string; name: string }) =>
          provider.id !== "credentials" && (
            <Button
              key={provider.name}
              size={page ? "md" : "xl"}
              w="full"
              mt=".75rem"
              variant="outline"
              onClick={() => onSignin(provider.id)}
              leftIcon={
                provider.name == "Facebook" ? (
                  <Box w="16px" h="16px" position="relative">
                    <Image src="/facebook.png" alt="" />
                  </Box>
                ) : provider.name == "Twitter" ? (
                  <Box w="16px" h="16px" position="relative">
                    <Image src="/twitter.png" alt="" />
                  </Box>
                ) : (
                  <Box w="16px" h="16px" position="relative">
                    <Image src="/google.png" alt="" />
                  </Box>
                )
              }
              borderColor={
                provider.name == "Facebook"
                  ? "#4267B2"
                  : provider.name == "Twitter"
                  ? "#1DA1F2"
                  : "#DB4437"
              }
              color={
                provider.name == "Facebook"
                  ? "#4267B2"
                  : provider.name == "Twitter"
                  ? "#1DA1F2"
                  : "#DB4437"
              }
              _hover={{
                bg:
                  provider.name == "Facebook"
                    ? "rgba(66,103,178,0.1)"
                    : provider.name == "Twitter"
                    ? "rgba(29,161,242,0.1)"
                    : "rgba(219,68,55,0.1)",
              }}
              _active={{
                bg:
                  provider.name == "Facebook"
                    ? "rgba(66,103,178,0.1)"
                    : provider.name == "Twitter"
                    ? "rgba(29,161,242,0.1)"
                    : "rgba(219,68,55,0.1)",
              }}
            >
              {provider.name}
            </Button>
          )
      )}

      <Box mt="2rem" textAlign="center">
        <StyledLink
          isExternal
          href={`${publicRuntimeConfig.basePath}/polityka_prywatnosci.pdf`}
        >
          <Text color="gray.500">{t("users.privacyPolicy")}</Text>
        </StyledLink>
        <StyledLink
          isExternal
          href={`${publicRuntimeConfig.basePath}/regulamin.pdf`}
        >
          <Text color="gray.500">{t("users.TOS")}</Text>
        </StyledLink>
      </Box>
    </>
  );
};

export default Signin;
